import { useLazyQuery, useQuery } from '@apollo/client';
import {
  getTranslatedText,
  Icon,
  SelectionSheet,
  Loader
} from '@nile/components';
import { GET_ALL_TEMPLATELIST } from '@nile/graphql';
import { GET_SOP } from '@nile/graphql/src/Query/sop';
import React, { useEffect, useState, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import { sortBy } from 'lodash';
import { Sheet, Button, Avatar, RadioButton } from '@superops/ui-design-system';
import { Timer } from '@styled-icons/remix-fill';
import styled from 'styled-components';

const TimerIcon = styled(Timer)`
  min-width: 16px;
  min-height: 16px;
  width: 16px;
  height: 16px;
  color: ${(props) => (props.danger ? 'red' : 'black')};
`;

const TemplateHolder = styled.div`
  .Polaris-Avatar {
    min-width: 2rem;
    &.Polaris-Avatar--sizeMedium {
      width: 2rem;
      height: 2rem;
    }
  }
`;

function CustomRadioButton(props) {
  const [value, setValue] = useState(props.selectedItems?.[0]?.id || '');
  const { options, updateParent, template } = props;
  const handleChange = useCallback((_checked, newValue) => {
    setValue(newValue);
    updateParent([options.find((item) => item.id === newValue)]);
  });
  return (
    <div className="flex col p20 scroll-in">
      {options.map((item) => {
        return (
          <div className="flex flex-align-center my10 g10">
            <RadioButton
              // label={item.name}
              id={item.id}
              name={item.name}
              checked={value === item.id}
              onChange={handleChange}
            />
            {template(item)}
          </div>
        );
      })}
    </div>
  );
}

function AssociateTasks(props) {
  const { intl, ticket, isRunbookAction } = props;
  const { loading, data } = useQuery(GET_ALL_TEMPLATELIST);
  const [allRunbookTasks, setRunbookTasks] = useState([]);
  const [
    getAssociatedRunbook,
    { loading: sopLoading, data: sopData, error: sopError }
  ] = useLazyQuery(GET_SOP, {
    fetchPolicy: 'network-only',
    variables: {
      sopId: ticket?.sop?.sopId
    },
    onCompleted({ getSOP }) {
      if (getSOP) {
        setRunbookTasks(
          sortBy(getSOP.taskTemplates, 'taskOrder').map(
            ({ taskOrder, taskTemplate }) => ({
              taskOrder,
              ...taskTemplate
            })
          )
        );
      }
    }
  });
  const context = props.context || {};
  const [selectedItems, setSelectedItems] = useState([]);
  const allTasks = data?.getTaskTemplateList || [];

  useEffect(() => {
    if (ticket?.sop) {
      getAssociatedRunbook();
    }
  }, []);

  useEffect(() => {
    if (allTasks.length > 0 && props?.setAllTasks) {
      props.setAllTasks(allTasks);
    }
  }, [allTasks]);

  if (loading || sopLoading)
    return (
      <div className="h100">
        <Loader />
      </div>
    );
  const sections = [];
  const runbookTasks = allRunbookTasks || [];
  const otherTasks = allTasks.filter(
    (el) => !runbookTasks.some((u) => u.taskTemplateId === el.taskTemplateId)
  );

  const runbookTasksSection = {
    name: 'runbookTasks',
    header: 'Runbook tasks',
    items: runbookTasks.map((el) => {
      return {
        ...el,
        id: el.taskTemplateId,
        name: el.title,
        label: el.title
      };
    })
  };

  const otherTasksSection = {
    name: 'otherTasks',
    header: getTranslatedText(
      intl,
      'ticket.details.tasks.create.allTasks.header'
    ),
    items: otherTasks.map((el) => {
      return {
        ...el,
        id: el.taskTemplateId,
        name: el.title,
        label: el.title
      };
    })
  };

  if (runbookTasks.length) {
    sections.push(runbookTasksSection);
  }

  sections.push(otherTasksSection);

  const updateParent = (selectedItem) => {
    props.updateTaskList(selectedItem);
  };

  /**
   * We render the name if it's present. Else we render firstName and lastName combined.
   * We're not fetching name because it would cause problems while sending back the data.
   */
  const template = (item) => {
    return (
      <TemplateHolder>
        <div>{item.name}</div>
        <div className="flex mt10 center">
          <div className="flex mr25 center">
            <Avatar customer size="extraSmall" name={item.name} />
            <div className="ml8">
              {item?.technician?.name || item?.technician?.firstName
                ? `${item?.technician?.firstName} ${item?.technician?.lastName}`
                : '--'}
            </div>
          </div>
          <div className="flex mr25 center">
            <TimerIcon />
            <div className="ml8">{item.estimatedTime || '--'}</div>
          </div>
        </div>
      </TemplateHolder>
    );
  };

  if (props.isRadio) {
    return (
      <CustomRadioButton
        options={otherTasksSection.items}
        field={props.field || { fieldProps: {} }}
        builderActions={props.builderActions || { updateFormField: () => {} }}
        updateParent={updateParent}
        selectedItems={selectedItems}
        template={template}
      />
    );
  }

  return (
    <SelectionSheet
      className="full-width full-height pd-y0 mt-m-20"
      template={template}
      sections={sections}
      actions={props.actions}
      meta={
        !isRunbookAction && {
          heading:
            props.heading ||
            getTranslatedText(
              intl,
              'ticket.details.tasks.create.associatetasks'
            ),
          selectedTitle:
            props.selectedTitle ||
            getTranslatedText(intl, 'ticket.details.tasks.create.select')
        }
      }
      selectedItems={selectedItems}
      toggleSheetActive={props.toggleSheetActive}
      updateParent={updateParent}
      hideSelectionSheetDoneBtn={props.hideSelectionSheetDoneBtn}
      isRunbookAction={props.isRunbookAction}
    />
  );
}

export default injectIntl(AssociateTasks);
